import { gradeColor } from "../../lib/finance-format";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * Financial Score gauge — big grade letter + score number + component breakdown.
 * Score is 0-1000 (like a credit score).
 */
export default function FinancialScore({ metrics }) {
  const { isDark } = useTheme();
  if (!metrics) return null;
  const score = metrics.financial_score ?? 0;
  const grade = metrics.grade ?? "F";
  const gradeLabel = metrics.grade_label ?? "";
  const color = gradeColor(grade);
  const pct = Math.min(1, score / 1000);
  const trackColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)";

  const components = [
    { key: "dti", label: "Deuda / Ingreso", value: metrics.score_components?.dti ?? 0, max: 300 },
    { key: "emergency_fund", label: "Fondo emergencia", value: metrics.score_components?.emergency_fund ?? 0, max: 200 },
    { key: "savings_rate", label: "Tasa de ahorro", value: metrics.score_components?.savings_rate ?? 0, max: 200 },
    { key: "interest_health", label: "Salud de intereses", value: metrics.score_components?.interest_health ?? 0, max: 150 },
    { key: "net_worth_health", label: "Patrimonio positivo", value: metrics.score_components?.net_worth_health ?? 0, max: 150 },
  ];

  // Semi-circular gauge geometry
  const R = 88;
  const CIRC = Math.PI * R; // half-circle length
  const dash = CIRC * pct;

  return (
    <div
      data-testid="financial-score-card"
      className="relative overflow-hidden rounded-lg border border-white/8 bg-[#0F1017] p-5 md:p-6"
      style={{
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.03), 0 8px 40px ${color}22`,
      }}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40 mb-4">
        Financial Score · CFO Rating
      </div>

      <div className="flex items-center gap-6">
        {/* Gauge */}
        <div className="relative shrink-0" style={{ width: 200, height: 120 }}>
          <svg viewBox="0 0 200 120" width="200" height="120">
            {/* Background arc */}
            <path
              d={`M 12 108 A ${R} ${R} 0 0 1 188 108`}
              fill="none"
              stroke={trackColor}
              strokeWidth="10"
              strokeLinecap="round"
            />
            {/* Value arc */}
            <path
              d={`M 12 108 A ${R} ${R} 0 0 1 188 108`}
              fill="none"
              stroke={color}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${CIRC}`}
              style={{ transition: "stroke-dasharray 700ms ease-out, stroke 200ms" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
            <div
              className="text-[64px] leading-none font-heading font-black tabular-nums tracking-tighter"
              style={{ color }}
              data-testid="score-grade"
            >
              {grade}
            </div>
          </div>
          <div className="absolute left-0 right-0 top-2 flex items-center justify-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
              {gradeLabel}
            </span>
          </div>
        </div>

        {/* Numeric score + range */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 mb-1">
            <span
              data-testid="score-value"
              className="text-4xl md:text-5xl font-mono font-black tabular-nums tracking-tight"
              style={{ color }}
            >
              {score}
            </span>
            <span className="text-sm font-mono text-white/40">/ 1000</span>
          </div>
          <div className="text-[11px] font-mono text-white/50 mb-4">
            Puntuación compuesta ponderada
          </div>

          {/* Component breakdown */}
          <div className="space-y-2">
            {components.map((c) => {
              const p = Math.round((c.value / c.max) * 100);
              return (
                <div key={c.key}>
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.14em] mb-1">
                    <span className="text-white/60">{c.label}</span>
                    <span className="text-white/40 tabular-nums">
                      {c.value}/{c.max}
                    </span>
                  </div>
                  <div className="h-1 w-full rounded-full bg-white/6 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${p}%`,
                        backgroundColor: p >= 75 ? "#10B981" : p >= 40 ? "#FACC15" : "#EF4444",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
